import { gql, useQuery } from "@apollo/client";

export type AttributionEventTypeValue =
  | "REGISTER_PAGE_VISITED"
  | "ACCOUNT_CREATED"
  | "MEMBERSHIP_SUBSCRIBED"
  | "MEMBERSHIP_UPGRADED";

export type AttributionVariantValue = "CONTROL" | "TEST" | "UNKNOWN";
export type MembershipTypeValue = "FREE" | "BASIC" | "GUILD_PLUS" | "UNKNOWN";

export interface AttributionEventRecord {
  eventId: string;
  idempotencyKey: string;
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  anonymousId?: string | null;
  eventType: AttributionEventTypeValue;
  accountType?: string | null;
  membershipType?: MembershipTypeValue | null;
  variant: AttributionVariantValue;
  sourceLandingPage?: string | null;
  referrerUrl?: string | null;
  occurredAt: string;
  createdAt: string;
}

export interface AttributionEventFiltersInput {
  eventType?: AttributionEventTypeValue;
  accountType?: string;
  membershipType?: MembershipTypeValue;
  variant?: AttributionVariantValue;
  userId?: string;
  anonymousId?: string;
  sourceLandingPage?: string;
  referrerUrl?: string;
  occurredFrom?: string;
  occurredTo?: string;
  searchText?: string;
}

interface GetAttributionEventsQueryData {
  getAttributionEvents: {
    nodes: AttributionEventRecord[];
    totalCount: number;
    page: number;
    limit: number;
    hasNextPage: boolean;
  };
}

interface GetAttributionEventsQueryVariables {
  page: number;
  limit: number;
  filters?: AttributionEventFiltersInput;
}

const GET_ATTRIBUTION_EVENTS_QUERY = gql`
  query GetAttributionEvents(
    $page: Int
    $limit: Int
    $filters: AttributionEventFiltersInput
  ) {
    getAttributionEvents(page: $page, limit: $limit, filters: $filters) {
      totalCount
      page
      limit
      hasNextPage
      nodes {
        eventId
        idempotencyKey
        userId
        userName
        userEmail
        anonymousId
        eventType
        accountType
        membershipType
        variant
        sourceLandingPage
        referrerUrl
        occurredAt
        createdAt
      }
    }
  }
`;

export const useGetAttributionEventsQuery = (
  variables: GetAttributionEventsQueryVariables
) => {
  const { data, loading, error, refetch } = useQuery<
    GetAttributionEventsQueryData,
    GetAttributionEventsQueryVariables
  >(GET_ATTRIBUTION_EVENTS_QUERY, {
    variables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  return {
    events: data?.getAttributionEvents.nodes ?? [],
    totalCount: data?.getAttributionEvents.totalCount ?? 0,
    page: data?.getAttributionEvents.page ?? variables.page,
    limit: data?.getAttributionEvents.limit ?? variables.limit,
    hasNextPage: data?.getAttributionEvents.hasNextPage ?? false,
    loading,
    error,
    refetch,
  };
};
